import { useState, useCallback } from "react";
import { ref, set, remove } from "firebase/database";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db, cloudstore } from "../api/firebase";
import { useAuth } from "../helpers/Auth";
import { useNavigate } from "react-router";
import uuid from "react-uuid";
import type { Project, Firing, Segment } from "../types";

const useProjectActions = () => {
  const { effectiveUID } = useAuth();
  const [editPending, setEditPending] = useState(false);
  const navigate = useNavigate();

  const userPath = () => `/userdata/${effectiveUID}`;

  const createProject = useCallback(
    async (formProps: any) => {
      formProps.id = uuid();

      const handleUpload = (photo: any, index: number) => {
        if (typeof photo.photo === "string") {
          return null;
        }
        return new Promise((resolve) => {
          const uploadName = userPath() + `/${formProps.id}-${index}.jpg`;
          uploadBytes(storageRef(cloudstore, uploadName), photo.photo[0])
            .then(() =>
              getDownloadURL(storageRef(cloudstore, uploadName)).then((url) => {
                formProps.photos[index].photo = url;
                resolve("Uploaded");
              })
            )
            .catch((e) => console.error("Error", e.message));
        });
      };

      if (formProps.photos) {
        await Promise.all(formProps.photos.map(handleUpload)).catch((e: any) =>
          console.error("Error", e.message)
        );
      }

      set(ref(db, userPath() + `/projects/${formProps.id}`), {
        ...formProps,
      });
      navigate(-1);
    },
    [effectiveUID, navigate]
  );

  const editProject = useCallback(
    async (id: string, formProps: any, ignoreNavigate: boolean = false) => {
      setEditPending(true);
      formProps.id = id;

      const handleUpload = (photo: any, index: number) => {
        if (typeof photo.photo === "string") {
          return null;
        }
        return new Promise((resolve) => {
          const uploadName = userPath() + `/${formProps.id}-${index}.jpg`;
          uploadBytes(storageRef(cloudstore, uploadName), photo.photo)
            .then(() =>
              getDownloadURL(storageRef(cloudstore, uploadName)).then((url) => {
                formProps.photos[index].photo = url;
                resolve("Uploaded");
              })
            )
            .catch((e) => console.error("Error", e.message));
        });
      };

      if (formProps.photos) {
        await Promise.all(formProps.photos.map(handleUpload)).catch((e: any) =>
          console.error("Error", e.message)
        );
      }

      set(ref(db, userPath() + `/projects/${formProps.id}`), {
        ...formProps,
      });
      setEditPending(false);

      if (!ignoreNavigate) {
        navigate(-1);
      }
    },
    [effectiveUID, navigate]
  );

  const deleteProject = useCallback(
    (id: string) => {
      remove(ref(db, userPath() + `/projects/${id}`));
      navigate("/projects");
    },
    [effectiveUID, navigate]
  );

  const duplicateProject = useCallback(
    (project: Project, firings: Firing[], segments: Segment[]) => {
      const newProjectId = uuid();
      const clonedProject = { ...project, id: newProjectId, name: "Copy of " + (project.name || ""), photos: [] };
      set(ref(db, userPath() + `/projects/${newProjectId}`), clonedProject);

      for (const firing of firings) {
        const newFiringId = uuid();
        const clonedFiring = { ...firing, id: newFiringId, project_id: newProjectId };
        set(ref(db, userPath() + `/firings/${newFiringId}`), clonedFiring);

        const firingSegments = segments.filter(s => s.firing_id === firing.id);
        for (const segment of firingSegments) {
          const newSegmentId = uuid();
          const clonedSegment = { ...segment, id: newSegmentId, firing_id: newFiringId };
          set(ref(db, userPath() + `/segments/${newSegmentId}`), clonedSegment);
        }
      }

      navigate(`/projects/${newProjectId}`);
    },
    [effectiveUID, navigate]
  );

  return { createProject, editProject, deleteProject, duplicateProject, editPending };
};

export default useProjectActions;
